import { createSlice } from '@reduxjs/toolkit'
import { parseJsonWithError, jsonToCsv, jsonToXml } from '../../utils/jsonHelpers'

const initialState = {
  rawInput: '',
  formatted: '',
  minified: '',
  csv: '',
  xml: '',
  error: null,        // { line, col, message } | null
  viewMode: 'formatted', // 'formatted' | 'minified' | 'tree' | 'csv' | 'xml'
  isValid: false,
}

const jsonSlice = createSlice({
  name: 'json',
  initialState,
  reducers: {
    // Live validation: runs on every input change.
    setRawInput: (state, action) => {
      state.rawInput = action.payload
      const trimmed = (action.payload || '').trim()
      if (!trimmed) {
        state.error = null
        state.isValid = false
        return
      }
      const result = parseJsonWithError(action.payload)
      if (result.valid) {
        state.isValid = true
        state.error = null
      } else {
        state.isValid = false
        state.error = { line: result.line, col: result.col, message: result.message }
      }
    },
    formatJson: (state) => {
      const result = parseJsonWithError(state.rawInput)
      if (result.valid) {
        state.formatted = JSON.stringify(result.data, null, 2)
        state.minified = JSON.stringify(result.data)
        state.error = null
        state.isValid = true
      } else {
        state.error = { line: result.line, col: result.col, message: result.message }
        state.isValid = false
        state.formatted = ''
        state.minified = ''
      }
    },
    minifyJson: (state) => {
      const result = parseJsonWithError(state.rawInput)
      if (result.valid) {
        state.minified = JSON.stringify(result.data)
        state.formatted = JSON.stringify(result.data, null, 2)
        state.error = null
        state.isValid = true
      } else {
        state.error = { line: result.line, col: result.col, message: result.message }
        state.isValid = false
        state.formatted = ''
        state.minified = ''
      }
    },
    validateJson: (state) => {
      const result = parseJsonWithError(state.rawInput)
      if (result.valid) {
        state.error = null
        state.isValid = true
      } else {
        state.error = { line: result.line, col: result.col, message: result.message }
        state.isValid = false
      }
    },
    convertToCsv: (state) => {
      const result = parseJsonWithError(state.rawInput)
      if (result.valid) {
        try {
          state.csv = jsonToCsv(result.data)
          state.error = null
          state.isValid = true
        } catch (e) {
          state.error = { message: 'This JSON cannot be converted to CSV (expected an object or array of objects).' }
        }
      } else {
        state.error = { line: result.line, col: result.col, message: result.message }
        state.isValid = false
        state.csv = ''
      }
    },
    convertToXml: (state) => {
      const result = parseJsonWithError(state.rawInput)
      if (result.valid) {
        state.xml = jsonToXml(result.data)
        state.error = null
        state.isValid = true
      } else {
        state.error = { line: result.line, col: result.col, message: result.message }
        state.isValid = false
        state.xml = ''
      }
    },
    setViewMode: (state, action) => {
      state.viewMode = action.payload
    },
    clearJsonState: (state) => {
      state.rawInput = ''
      state.formatted = ''
      state.minified = ''
      state.csv = ''
      state.xml = ''
      state.error = null
      state.viewMode = 'formatted'
      state.isValid = false
    },
  },
})

export const {
  setRawInput,
  formatJson,
  minifyJson,
  validateJson,
  convertToCsv,
  convertToXml,
  setViewMode,
  clearJsonState,
} = jsonSlice.actions

export default jsonSlice.reducer